import { useEffect, useRef, useState } from "react";
import {
  CONSENT_ITEM_IDS,
  allChecked,
  emptyChecks,
  localizedConsentBody,
  localizedConsentText,
  toggleAll,
} from "@fiket/booth-schema";
import type { ConsentChecks, ConsentCheckId, ConsentItemId } from "@fiket/booth-schema";
import { useRouter } from "../../store/router";
import { useBoothConfig } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { trackVisitEnd } from "../../telemetry/activity";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Lines } from "../../components/ui/Lines";
import { RingSpinner } from "../../components/ui/RingSpinner";
import { ConsentBodyModal } from "./ConsentBodyModal";
import { consentPayload, ctaEnabled } from "./consentFlow";
import { postConsent } from "./consentApi";

/**
 * STAGE · Consent (BOOTH-66, 스펙 §4.2·4.3 — C안 "항목별 체크 + 전체 동의") — 촬영 직전 개인정보 동의.
 * 체크 카드 4장(수집·이용 / 해외 처리 / 전시 표시 / 연령·동반자 확인) 전체가 터치 타깃이고 기본 미체크,
 * 앞 세 장은 "보기" 로 전문 모달을 연다. 우상단 "전체 동의" 가 네 개를 한 번에 켜고(모두 켜짐이면 모두 해제),
 * CTA 는 필수 항목 전부 체크일 때만 활성.
 *
 * CTA → `POST /api/booth/:slug/consent` → `consentId` 를 store 에. 실패는 1회 재시도 후에도 실패하면
 * **촬영을 막지 않고** `consentPending` 에 payload 를 들고 preview 로 간다 — 세션 생성 본문이 그 증적을 대신 싣는다.
 * "동의하지 않음" → `visit_end{reason:"declined"}` 기록 후 어트랙트. 라벨·전문은 발행된 동의서에서,
 * 제목·버튼 문구는 키오스크 카탈로그(`consent.*`)에서 온다. 언어는 세션 언어 + ko 폴백(동의서는 비면 안 됨).
 */
export function ConsentView() {
  const go = useRouter((s) => s.go);
  const resetSession = useRouter((s) => s.resetSession);
  const visitId = useRouter((s) => s.visitId);
  const setConsentId = useRouter((s) => s.setConsentId);
  const setConsentPending = useRouter((s) => s.setConsentPending);
  const doc = useBoothConfig((s) => s.config?.consent ?? null);
  const slug = useBoothConfig((s) => s.slug);
  const { t, language } = useI18n();

  const [checks, setChecks] = useState<ConsentChecks>(emptyChecks);
  const [busy, setBusy] = useState(false);
  const [openItem, setOpenItem] = useState<ConsentItemId | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // 화면을 떠나면 진행 중인 POST 를 끊는다 — 손님이 CTA 를 누른 뒤 뒤로 가면(busy 중에도 뒤로가기는 살아 있다)
  // 늦게 도착한 응답이 go("preview") 로 다시 끌고 가고 재시도도 계속 돌았다
  useEffect(() => () => abortRef.current?.abort(), []);

  // 발행본이 없으면 이 화면을 띄우지 않는다 — StyleSelect 가 이미 걸러내지만(nextRouteAfterStyle)
  // 설정이 늦게 와서 직접 진입한 경우를 대비한 안전망
  useEffect(() => {
    if (!doc) go("preview");
  }, [doc, go]);

  if (!doc) return null;

  const enabled = ctaEnabled(doc, checks, busy);
  const modalOpen = openItem !== null;

  const toggle = (id: ConsentCheckId) => {
    if (busy) return;
    setChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /** CTA — 동의 레코드를 만들고 촬영으로. 실패해도 손님을 막지 않는다(§4.3) */
  const submit = async () => {
    if (!enabled) return;
    const payload = consentPayload(doc, checks, language, visitId);
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;
    /** await 뒤에는 반드시 확인한다 — 이미 이 화면을 떠났으면 store 도 라우트도 건드리지 않는다 */
    const left = () => signal.aborted || useRouter.getState().route !== "consent";

    if (slug) {
      // 네트워크·5xx·타임아웃(시도당 10초)은 1회 자동 재시도
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const { consentId } = await postConsent(slug, payload, signal);
          if (left()) return;
          setConsentId(consentId);
          go("preview");
          return;
        } catch (err) {
          if (left()) return;
          if (attempt === 1) console.error("[Consent] 동의 저장 실패 — 세션 생성 때 다시 보낸다", err);
        }
      }
    } else {
      console.error("[Consent] 부스 slug 없음 — 세션 생성 때 동의를 보낸다");
    }
    if (left()) return;
    // 증적을 잃지 않고 진행 — POST /api/sessions 본문이 payload 를 싣는다
    setConsentPending(payload);
    go("preview");
  };

  const decline = () => {
    trackVisitEnd("declined");
    resetSession();
  };

  const item = openItem ? doc.items[openItem] : null;

  return (
    <div className="view view--consent">
      {/* 모달이 열리면 뒤 내용은 보조기기·포커스에서 제외한다 — aria-hidden 만으론 버튼이 포커스 가능해 inert 를 함께 */}
      <div className="view__head" aria-hidden={modalOpen} inert={modalOpen}>
        <BackButton label={t.consent.back} onClick={() => go("styleSelect")} />
        <h1 className="view__title"><Lines text={t.consent.title} /></h1>
      </div>

      <div className="view__body" aria-hidden={modalOpen} inert={modalOpen}>
        {/* 전체 동의 — 목록과 같은 폭의 체크 행(사용자 피드백 2026-10-21: 버튼보다 체크박스가 자연스럽다). 네 개가 다 켜지면 함께 켜지고, 켜진 상태에서 누르면 모두 해제 */}
        <div className="consent-all">
          <div
            className="consent-item consent-item--all"
            role="checkbox"
            aria-checked={allChecked(checks)}
            aria-disabled={busy || undefined}
            tabIndex={0}
            onClick={() => { if (!busy) setChecks(toggleAll); }}
            onKeyDown={(e) => {
              if (!busy && (e.key === " " || e.key === "Enter")) { e.preventDefault(); setChecks(toggleAll); }
            }}
          >
            <CheckBox on={allChecked(checks)} />
            <span className="consent-item__text">{t.consent.allAgree}</span>
          </div>
        </div>

        <ul className="consent-list">
          {CONSENT_ITEM_IDS.map((id) => {
            const text = doc.items[id];
            return (
              <li key={id}>
                <div
                  className="consent-item"
                  role="checkbox"
                  aria-checked={checks[id]}
                  tabIndex={0}
                  onClick={() => toggle(id)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(id); }
                  }}
                >
                  <CheckBox on={checks[id]} />
                  <span className="consent-item__text">
                    {text.required ? <b className="consent-item__badge">{t.consent.required}</b> : null}
                    {localizedConsentText(text, language)}
                  </span>
                  <button
                    type="button"
                    className="consent-item__view"
                    // 카드 전체가 체크 토글이라 "보기" 는 전파를 멈춘다
                    onClick={(e) => { e.stopPropagation(); setOpenItem(id); }}
                  >
                    {t.consent.view}
                  </button>
                </div>
              </li>
            );
          })}

          {/* 연령·동반자 확인 — 전문 없음(라벨만), 배지 없음. required 속성 없이 항상 필수 */}
          <li>
            <div
              className="consent-item"
              role="checkbox"
              aria-checked={checks.age}
              tabIndex={0}
              onClick={() => toggle("age")}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle("age"); }
              }}
            >
              <CheckBox on={checks.age} />
              <span className="consent-item__text">{localizedConsentText(doc.age, language)}</span>
            </div>
          </li>
        </ul>
      </div>

      {/* 미동의 · 동의 — 결과 화면과 같은 짝 버튼 한 줄 (사용자 피드백 2026-10-21: 두 줄로 흩어진 CTA·링크를 한 줄로) */}
      <div className="view__cta" aria-hidden={modalOpen} inert={modalOpen}>
        <div className="actions actions--pair">
          <Button variant="secondary" onClick={decline} disabled={busy}>{t.consent.decline}</Button>
          <Button onClick={() => void submit()} disabled={!enabled}>
            {busy ? <RingSpinner size={28} /> : null}
            {t.consent.cta}
          </Button>
        </div>
      </div>

      {item ? (
        <ConsentBodyModal
          title={localizedConsentText(item, language)}
          body={localizedConsentBody(item, language)}
          processor={t.consent.processor(doc.processorName)}
          contact={t.consent.contact(doc.processorContact)}
          refusal={t.consent.refusal}
          closeLabel={t.consent.close}
          onClose={() => setOpenItem(null)}
        />
      ) : null}
    </div>
  );
}

/** 36px 체크 상자 — 카드가 토글을 쥐므로 input 이 아닌 표시 전용(상태는 .consent-item aria-checked) */
function CheckBox({ on }: { on: boolean }) {
  return (
    <span className="consent-item__check" aria-hidden>
      {on ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      ) : null}
    </span>
  );
}
